/**
 * The register behind the Cortex console's Protocols tab: the MRT protocols
 * written for plays, and the validations of behavioural measures against
 * distal outcomes. Entries are checked into this file, because an entry is a
 * reviewed document and the review happens on the pull request that adds it.
 *
 * The console renders this read-only. The tunables editor reads the protocol
 * status to decide whether a probability may leave the learning default (O9),
 * so `approved` is a word with teeth: it is only set by a reviewed change.
 *
 * Template and checklist: docs/src/content/docs/cortex-mrt-protocol.md.
 * Register rules: docs/src/content/docs/cortex-validation-register.md.
 */
export const PROTOCOL_STATUSES = ["draft", "review", "approved", "retired"];
/** The state snapshot buckets a protocol may name as moderators. Mirrors the snapshot's fields. */
export const PROTOCOL_MODERATORS = [
    "timeOfDay",
    "dayType",
    "wearable",
    "receptivity",
    "budget",
    "tenure",
    "pushAllowed",
    "cohort",
    "pace",
    "week",
];
export const VALIDATION_STATUSES = ["proposed", "in_progress", "validated", "withdrawn"];
/**
 * Every protocol written so far. Nothing is approved: every play runs at the
 * learning default until an entry here says otherwise.
 */
export const MRT_PROTOCOLS = [
    {
        id: "take-walk-steps-v1",
        play: "rule.take_walk",
        title: "Does the walk ask move the next half hour's steps?",
        owner: "Product, HealthOS",
        status: "review",
        decisionPoints: "Ticks where the take_walk cue fires inside the member's delivery windows. Ticks stopped by the attention budget, the family cap or the refractory window are out of scope and counted from the stopped rows.",
        availabilityRule: "The member is not in a calendar meeting and quiet hours are not forced. This is what sets the row's available flag. Wearable freshness is a moderator, not part of availability.",
        proximalOutcome: {
            metric: "steps",
            windowMinutes: 30,
            hypothesis: "A walk taken when asked adds to the day's steps. If it only shifts steps the member would have taken anyway, the lift reads as zero.",
        },
        contrast: { kind: "send_vs_hold" },
        randomisation: { sendProbability: 0.8, floor: 0.2, fixed: true },
        moderators: ["timeOfDay", "wearable"],
        sample: { targetLift: 0.1, rowsNeeded: null, expectedWindowDays: null },
        guardrails: [
            { measure: "dismissed over seen", stopAt: "above 20 percent over 30 days" },
            { measure: "unseen share", stopAt: "above 50 percent over 30 days" },
        ],
        analysisPlan: "estimateLift over the window, standard errors clustered by member, regression adjusted on the pre-offer step covariates once each arm has 20 rows carrying them. The sample is planned on the magnitude, steps in the window. Primary estimate drops rows with no reading and reports the share per arm; sensitivity treats missing as no change. A positive interval leads to no change; an interval below zero retires the variant; a split that holds only in the morning narrows the cue.",
    },
    {
        id: "short-sleep-onset-v1",
        play: "rule.pushed_on_short_sleep",
        title: "Does the earlier-night ask bring sleep forward after a short night?",
        owner: "Product, HealthOS",
        status: "draft",
        decisionPoints: "Evening ticks from 17:00 local where last night's sleep fell more than half an hour short of the member's target and today's steps are above 1.2 times their usual. At most one per member per night. Ticks stopped by the attention budget, the family cap or the refractory window are out of scope and counted from the stopped rows.",
        availabilityRule: "The member is not in a calendar meeting and quiet hours are not forced. This is what sets the row's available flag.",
        proximalOutcome: {
            metric: "sleep_onset",
            windowMinutes: 720,
            hypothesis: "Getting to sleep sooner after a short night and a busy day gives back some of the sleep that was lost. If the ask changes nothing, minutes to sleep read the same in both arms.",
        },
        contrast: { kind: "send_vs_hold" },
        randomisation: { sendProbability: 0.8, floor: 0.2, fixed: true },
        moderators: ["dayType", "receptivity"],
        sample: { targetLift: 0.1, rowsNeeded: null, expectedWindowDays: null },
        guardrails: [
            { measure: "dismissed over seen", stopAt: "above 20 percent over 30 days" },
            { measure: "unseen share", stopAt: "above 50 percent over 30 days" },
            {
                measure: "nights with no sleep record after the offer",
                stopAt: "above 40 percent over 30 days",
            },
        ],
        analysisPlan: "estimateLift on the score (asleep within 240 minutes of the offer), standard errors clustered by member, regression adjusted on the pre-offer sleep covariates (offer minute, last night's onset, usual onset, same night last week, last night's hours) once each arm has 20 rows carrying them. The sample is planned on the magnitude, minutes from the offer to sleep onset, where lower is better. With at most one decision point per member per night, the window will be long. Primary estimate drops nights with no sleep record and reports the share per arm; sensitivity treats a missing night as not asleep in good time. A positive interval leads to no change; an interval below zero retires the variant; an effect that holds only on work nights narrows the cue to work nights.",
    },
];
/**
 * Measures named for validation. Each is one concept, for one member, over
 * weeks, against their own baseline. Nothing is validated, and nothing may be
 * called a digital endpoint until an entry here says `validated`.
 */
export const VALIDATION_REGISTER = [
    {
        id: "steps-weekly",
        measure: "steps",
        definition: "Daily steps over a rolling 28 days against the member's prior 28 day median.",
        completeness: {
            usableUnit: "A day counts when the wearable synced it and its total is at least 500 steps. A lower total is read as the device not worn, never as a sedentary day.",
            minimumUsable: 20,
            windowUnits: 28,
        },
        distalOutcome: "Not yet named.",
        partner: null,
        status: "proposed",
        startedOn: null,
        notes: "The one measure that exists today. Its distal outcome is the partner's to name.",
    },
    {
        id: "glp1-dose-consistency",
        measure: "dose_consistency",
        definition: "Share of scheduled GLP-1 doses with a self-report inside the dose window, over 28 days. Self-reported, never proportion of days covered.",
        completeness: {
            usableUnit: "A scheduled dose counts once its dose window has closed and the schedule was on record for the whole window. A dose with no report is a missed dose, not missing data, because the report is the measure.",
            minimumUsable: 4,
            windowUnits: 4,
        },
        distalOutcome: "Not yet named.",
        partner: null,
        status: "proposed",
        startedOn: null,
        notes: "Specified in the feature set (C12); the measure itself is not built yet.",
    },
    {
        id: "sleep-onset-regularity",
        measure: "sleep_onset_regularity",
        definition: "Standard deviation of sleep onset time over 14 nights, against the prior 14.",
        completeness: {
            usableUnit: "A night counts when the wearable recorded a main sleep with an onset between 18:00 and 06:00 local and a length between 3 and 14 hours. Naps are not nights; a record outside those bounds is dropped as anomalous.",
            minimumUsable: 10,
            windowUnits: 14,
        },
        distalOutcome: "Not yet named.",
        partner: null,
        status: "proposed",
        startedOn: null,
        notes: "Specified in the feature set (C12); the measure itself is not built yet.",
    },
    {
        id: "desk-break-frequency",
        measure: "desk_break_frequency",
        definition: "Breaks from sitting per working hour over a week, against the prior four weeks.",
        completeness: {
            usableUnit: "A working day counts when the calendar marks it as worked and the wearable's activity timeline covers at least six of its working hours.",
            minimumUsable: 3,
            windowUnits: 5,
        },
        distalOutcome: "Not yet named.",
        partner: null,
        status: "proposed",
        startedOn: null,
        notes: "Specified in the feature set (C12); the measure itself is not built yet.",
    },
    {
        id: "active-minutes",
        measure: "active_minutes",
        definition: "Active minutes per day over 28 days against the prior 28 day median.",
        completeness: {
            usableUnit: "A day counts when the wearable published an activity timeline covering at least ten hours of it. A shorter timeline is the device not worn, never an inactive day.",
            minimumUsable: 20,
            windowUnits: 28,
        },
        distalOutcome: "Not yet named.",
        partner: null,
        status: "proposed",
        startedOn: null,
        notes: "Specified in the feature set (C12); the measure itself is not built yet.",
    },
];
/**
 * Checks a register entry's completeness rule can be met. Run by the tests
 * over the checked-in list, beside the protocol checks.
 */
export function validationProblems(v) {
    const problems = [];
    const c = v.completeness;
    if (c.usableUnit.trim() === "")
        problems.push("usable unit is not stated");
    if (!Number.isInteger(c.windowUnits) || c.windowUnits <= 0) {
        problems.push("window units is not a positive whole number");
    }
    if (!Number.isInteger(c.minimumUsable) || c.minimumUsable <= 0) {
        problems.push("minimum usable is not a positive whole number");
    }
    if (c.minimumUsable > c.windowUnits)
        problems.push("minimum usable exceeds the window");
    return problems;
}
/** The approved protocol for a play, if there is one. What the probability gate reads. */
export function approvedProtocolFor(play) {
    return MRT_PROTOCOLS.find((p) => p.play === play && p.status === "approved") ?? null;
}
/**
 * Checks a register entry says what the template requires. Run by the tests
 * over the checked-in list, so a protocol cannot merge half written.
 */
export function protocolProblems(p) {
    const problems = [];
    if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(p.id))
        problems.push("id is not kebab-case");
    if (!p.play.startsWith("rule."))
        problems.push("play is not a rule id");
    if (p.randomisation.sendProbability > 1 - p.randomisation.floor) {
        problems.push("send probability crosses the hold floor");
    }
    if (p.randomisation.sendProbability <= 0)
        problems.push("send probability is not positive");
    if (p.moderators.length > 3)
        problems.push("more than three moderators");
    if (new Set(p.moderators).size !== p.moderators.length)
        problems.push("a moderator repeats");
    if (p.proximalOutcome.windowMinutes <= 0)
        problems.push("window is not positive");
    if (p.proximalOutcome.hypothesis.trim() === "")
        problems.push("hypothesis is empty");
    if (p.guardrails.length === 0)
        problems.push("no guardrails");
    if (p.sample.targetLift <= 0)
        problems.push("target lift is not positive");
    if (p.status === "approved" && p.sample.rowsNeeded === null) {
        problems.push("approved without a sample requirement");
    }
    if (p.status === "approved" && p.sample.expectedWindowDays === null) {
        problems.push("approved without an expected window");
    }
    return problems;
}
//# sourceMappingURL=cortex-protocols.js.map